import ClientAdapter from './contracts/ClientAdapter';
import Item from '../entities/contracts/Item';
import Provider from '../entities/contracts/Provider';
import ItemImpl from '../entities/ItemImpl';
import ItemType from '../enums/ItemType';
import Offer from '../entities/contracts/Offer';
import OfferImpl from '../entities/OfferImpl';
import ProviderImpl from '../entities/ProviderImpl';
import PresentationType from '../enums/PresentationTypes';
import LocaleOption from "../entities/contracts/LocaleOption";
import LocaleOptionImpl from "../entities/LocaleOptionImpl";
import {prepUrl} from "../../utils/url";

export default class JustWatchClientAdapter implements ClientAdapter {

    private readonly API_BASE_URL = "https://justwatch.p.rapidapi.com/";
    private locale: string = "en_US";
    private static providers: any | null = null;

    public setLocale(value: string) {
        this.locale = value;
    }

    public async searchMoviesAndTvShows(query: string): Promise<Array<Item>> {
        const bodyParam = {
            query, content_types: ["show", "movie"]
        };

        return this.fetchApi("GET", `titles/${this.locale}/popular`, {body: JSON.stringify(bodyParam)})
            .then(async json => {
                if(JustWatchClientAdapter.providers === null) {
                    const jsonProviders: any = await this.fetchApi("GET", `providers/locale/${this.locale}`);
                    JustWatchClientAdapter.providers = JustWatchClientAdapter.convertFetchProvidersDataToProviders(jsonProviders);
                }

                return JustWatchClientAdapter.convertFetchDataToItemsArray(json);
            });
    }

    public async getAvailableLocales(): Promise<Array<LocaleOption>> {
        return this.fetchApi("GET", "/locales/state")
            .then(json => {
                return JustWatchClientAdapter.convertFetchDataToLocaleItemsArray(json)
                    .sort((a, b) => {
                        if(a.getName() < b.getName())
                            return -1;
                        if(a.getName() > b.getName())
                            return 1;
                        return 0;
                    });
            });
    }

    private static convertFetchProvidersDataToProviders(jsonProviders: any): Object {
        const providers:any  = {};

        for(const jsonProvider of jsonProviders) {
            const provider = new ProviderImpl();
            provider.setId(jsonProvider.id);
            provider.setName(jsonProvider.clear_name);
            const iconUrl = `https://images.justwatch.com${jsonProvider.icon_url}`.replace("{profile}", "s25");
            provider.setIconUrl(iconUrl);

            providers[jsonProvider.id] = provider;
        }

        return providers;
    }

    private static convertFetchDataToItemsArray(json: any): Array<Item> {
        const itemsArray = new Array<Item>();

        for(const jsonItem of json.items) {
            const item: Item = new ItemImpl();
            item.setId(jsonItem.id);
            item.setTitle(jsonItem.title);
            item.setUrl(`https://www.justwatch.com${jsonItem.full_path}`);
            const posterUrl = `https://images.justwatch.com${jsonItem.poster}`
                .replace("{profile}", "s592");
            item.setPosterUrl(posterUrl);
            item.setReleaseYear(jsonItem.original_release_year);
            item.setType(jsonItem.object_type === "show" ? ItemType.Show : ItemType.Movie);
            JustWatchClientAdapter.setFetchScoringDataToItem(jsonItem.scoring || null, item);
            JustWatchClientAdapter.setFetchOffersDataToItem(jsonItem.offers || null, item);
            itemsArray.push(item);
        }

        return itemsArray;
    }

    private static setFetchScoringDataToItem(jsonScoringData: Array<any> | null, item: Item): void {
        if(jsonScoringData === null)
            return;

        for(const jsonScore of jsonScoringData) {
            switch(jsonScore.provider_type) {
                case "tmdb:score":
                    item.setTmdbScore(jsonScore.value);
                    break;
                case "imdb:score":
                    item.setImdbScore(jsonScore.value);
                    break;
            }
        }
    }

    private static setFetchOffersDataToItem(jsonOffersData: Array<any> | null, item: Item): void {
        if(jsonOffersData === null)
            return;

        const offersArray = new Array<Offer>();
        for(const jsonOffer of jsonOffersData) {
            const provider: Provider = JustWatchClientAdapter.providers[jsonOffer.provider_id];
            const offer: Offer = new OfferImpl();
            const offerId = `${item.getId()}_${jsonOffersData.indexOf(jsonOffer)}`;
            offer.setId(offerId);
            offer.setProvider(provider);
            offer.setCurrency(jsonOffer.currency);
            let presentationType: PresentationType = PresentationType.Unknown;
            switch(jsonOffer.presentation_type) {
                case "sd":
                    presentationType = PresentationType.Sd;
                    break;
                case "hd":
                    presentationType = PresentationType.Hd;
                    break;
                case "4k":
                    presentationType = PresentationType.FourK;
                    break;
            }
            offer.setPresentationType(presentationType);
            offer.setPrice(jsonOffer.retail_price || null);
            let url: string | null = jsonOffer.urls.standard_web || null;
            if(url !== null)
                url = prepUrl(url);
            offer.setUrl(url);
            offersArray.push(offer);
        }

        item.setOffers(offersArray);
    }

    private static convertFetchDataToLocaleItemsArray(json: any): Array<LocaleOption> {
        const localeOptionsArray = new Array<LocaleOption>();
        for(const jsonLocaleOption of json) {
            localeOptionsArray.push(new LocaleOptionImpl(jsonLocaleOption.full_locale, jsonLocaleOption.country));
        }
        return localeOptionsArray;
    }

    private async fetchApi(method: string, endpoint: string, params: any = {}):Promise<any> {
        const headers = new Headers();
        headers.append("x-rapidapi-host", "justwatch.p.rapidapi.com");
        headers.append("x-rapidapi-key", "qRzwauCUkQmshqr7olFZYP5N8Kp2p1RmetIjsnOfzSDtMyoIWj");
        headers.append("content-type", "application/json");

        let paramsString = "";
        for(let key in params) {
            paramsString += `${key}=${encodeURIComponent(params[key])}&`;
        }
        if(paramsString) {
            paramsString = paramsString.substring(0, paramsString.length - 1);
            paramsString = `?${paramsString}`;
        }

        return fetch(`${this.API_BASE_URL}${endpoint}${paramsString}`, {method, headers})
            .then(response => response.json());
    }
}